import { useState, useRef, useEffect, useCallback } from 'react';
import { Stage, Layer, Rect, Circle, Group, Text, Line, Transformer } from 'react-konva';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Plus, Trash2, Save, Printer, ZoomIn, ZoomOut, RotateCw, 
  Grid3X3, Users, Download, Upload, Circle as CircleIcon,
  Square, RectangleHorizontal, Music, Camera, Utensils, Gift,
  Wine, Theater, Move, Edit2, UserPlus, X, Check, Maximize2, List
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Object type configurations
const OBJECT_TYPES = {
  round_table: { 
    name: 'Round Table', 
    icon: CircleIcon, 
    defaultWidth: 80, 
    defaultHeight: 80, 
    defaultSeats: 8,
    color: '#4a5568',
    hasSeats: true
  },
  square_table: { 
    name: 'Square Table', 
    icon: Square, 
    defaultWidth: 70, 
    defaultHeight: 70, 
    defaultSeats: 4,
    color: '#4a5568',
    hasSeats: true
  },
  rect_table: { 
    name: 'Rectangular Table', 
    icon: RectangleHorizontal, 
    defaultWidth: 120, 
    defaultHeight: 60, 
    defaultSeats: 6,
    color: '#4a5568',
    hasSeats: true
  },
  head_table: { 
    name: 'Head Table', 
    icon: RectangleHorizontal, 
    defaultWidth: 200, 
    defaultHeight: 50, 
    defaultSeats: 10,
    color: '#6b46c1',
    hasSeats: true
  },
  dj_booth: { 
    name: 'DJ Booth', 
    icon: Music, 
    defaultWidth: 100, 
    defaultHeight: 60, 
    defaultSeats: 0,
    color: '#3182ce',
    hasSeats: false
  },
  photobooth: { 
    name: 'Photo Booth', 
    icon: Camera, 
    defaultWidth: 80, 
    defaultHeight: 80, 
    defaultSeats: 0,
    color: '#d53f8c',
    hasSeats: false
  },
  dance_floor: { 
    name: 'Dance Floor', 
    icon: Maximize2, 
    defaultWidth: 200, 
    defaultHeight: 200, 
    defaultSeats: 0,
    color: '#38a169',
    hasSeats: false
  },
  buffet: { 
    name: 'Buffet Table', 
    icon: Utensils, 
    defaultWidth: 150, 
    defaultHeight: 40, 
    defaultSeats: 0,
    color: '#dd6b20',
    hasSeats: false
  },
  gift_table: { 
    name: 'Gift Table', 
    icon: Gift, 
    defaultWidth: 100, 
    defaultHeight: 50, 
    defaultSeats: 0,
    color: '#9f7aea',
    hasSeats: false
  },
  bar: { 
    name: 'Bar', 
    icon: Wine, 
    defaultWidth: 120, 
    defaultHeight: 40, 
    defaultSeats: 0,
    color: '#e53e3e',
    hasSeats: false
  },
  stage: { 
    name: 'Stage', 
    icon: Theater, 
    defaultWidth: 250, 
    defaultHeight: 100, 
    defaultSeats: 0,
    color: '#2d3748',
    hasSeats: false
  }
};

// Table Shape Component
const TableShape = ({ obj, isSelected, onSelect, onChange, onDblClick }) => {
  const shapeRef = useRef();
  const trRef = useRef();
  const config = OBJECT_TYPES[obj.type];
  
  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  const handleDragEnd = (e) => {
    onChange({
      ...obj,
      x: e.target.x(),
      y: e.target.y()
    });
  };

  const handleTransformEnd = () => {
    const node = shapeRef.current;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    
    node.scaleX(1);
    node.scaleY(1);
    
    onChange({
      ...obj,
      x: node.x(),
      y: node.y(),
      width: Math.max(30, node.width() * scaleX),
      height: Math.max(30, node.height() * scaleY),
      rotation: node.rotation()
    });
  };

  // Calculate seat positions for tables
  const getSeatPositions = () => {
    if (!config.hasSeats || obj.seats === 0) return [];
    const seats = [];
    const seatRadius = 12;
    
    if (obj.type === 'round_table') {
      const radius = obj.width / 2 + seatRadius + 5;
      for (let i = 0; i < obj.seats; i++) {
        const angle = (2 * Math.PI * i) / obj.seats - Math.PI / 2;
        seats.push({
          x: Math.cos(angle) * radius,
          y: Math.sin(angle) * radius,
          number: i + 1
        });
      }
    } else {
      // Rectangular/square tables - seats around edges
      const perSide = Math.ceil(obj.seats / 4);
      const topSeats = Math.min(perSide, obj.seats);
      const remaining = obj.seats - topSeats;
      const bottomSeats = Math.min(perSide, remaining);
      const leftSeats = Math.min(Math.ceil((remaining - bottomSeats) / 2), remaining - bottomSeats);
      const rightSeats = remaining - bottomSeats - leftSeats;
      
      let seatNum = 1;
      
      // Top side
      for (let i = 0; i < topSeats; i++) {
        seats.push({
          x: -obj.width/2 + (obj.width / (topSeats + 1)) * (i + 1),
          y: -obj.height/2 - seatRadius - 5,
          number: seatNum++
        });
      }
      
      // Bottom side
      for (let i = 0; i < bottomSeats; i++) {
        seats.push({
          x: -obj.width/2 + (obj.width / (bottomSeats + 1)) * (i + 1),
          y: obj.height/2 + seatRadius + 5,
          number: seatNum++
        });
      }
      
      // Left side
      for (let i = 0; i < leftSeats; i++) {
        seats.push({
          x: -obj.width/2 - seatRadius - 5,
          y: -obj.height/2 + (obj.height / (leftSeats + 1)) * (i + 1),
          number: seatNum++
        });
      }
      
      // Right side
      for (let i = 0; i < rightSeats; i++) {
        seats.push({
          x: obj.width/2 + seatRadius + 5,
          y: -obj.height/2 + (obj.height / (rightSeats + 1)) * (i + 1),
          number: seatNum++
        });
      }
    }
    
    return seats;
  };

  const seats = getSeatPositions();
  const assignedGuests = obj.guests || [];

  return (
    <>
      <Group
        ref={shapeRef}
        x={obj.x}
        y={obj.y}
        rotation={obj.rotation || 0}
        draggable
        onClick={onSelect}
        onTap={onSelect}
        onDblClick={onDblClick}
        onDblTap={onDblClick}
        onDragEnd={handleDragEnd}
        onTransformEnd={handleTransformEnd}
      >
        {/* Main shape */}
        {obj.type === 'round_table' ? (
          <Circle
            radius={obj.width / 2}
            fill={config.color}
            stroke={isSelected ? '#06b6d4' : '#1a202c'}
            strokeWidth={isSelected ? 3 : 2}
            shadowColor="black"
            shadowBlur={isSelected ? 10 : 5}
            shadowOpacity={0.3}
          />
        ) : obj.type === 'dance_floor' ? (
          <>
            <Rect
              x={-obj.width / 2}
              y={-obj.height / 2}
              width={obj.width}
              height={obj.height}
              fill={config.color}
              stroke={isSelected ? '#06b6d4' : '#1a202c'}
              strokeWidth={isSelected ? 3 : 2}
              cornerRadius={5}
              opacity={0.7}
            />
            {/* Dance floor pattern */}
            {[...Array(Math.floor(obj.width / 40))].map((_, i) => (
              [...Array(Math.floor(obj.height / 40))].map((_, j) => (
                <Rect
                  key={`${i}-${j}`}
                  x={-obj.width / 2 + i * 40 + 5}
                  y={-obj.height / 2 + j * 40 + 5}
                  width={35}
                  height={35}
                  fill={(i + j) % 2 === 0 ? '#2d3748' : '#4a5568'}
                  cornerRadius={2}
                />
              ))
            ))}
          </>
        ) : (
          <Rect
            x={-obj.width / 2}
            y={-obj.height / 2}
            width={obj.width}
            height={obj.height}
            fill={config.color}
            stroke={isSelected ? '#06b6d4' : '#1a202c'}
            strokeWidth={isSelected ? 3 : 2}
            cornerRadius={obj.type === 'stage' ? 0 : 5}
            shadowColor="black"
            shadowBlur={isSelected ? 10 : 5}
            shadowOpacity={0.3}
          />
        )}
        
        {/* Label */}
        <Text
          text={obj.label || config.name}
          x={obj.type === 'round_table' ? -obj.width/2 : -obj.width / 2}
          y={obj.type === 'round_table' ? -8 : -obj.height / 2 + obj.height/2 - 8}
          width={obj.width}
          align="center"
          fill="white"
          fontSize={12}
          fontStyle="bold"
        />
        
        {/* Seats */}
        {seats.map((seat, idx) => {
          const guest = assignedGuests.find(g => g.seat_number === seat.number);
          return (
            <Group key={idx}>
              <Circle
                x={seat.x}
                y={seat.y}
                radius={12}
                fill={guest ? '#38a169' : '#718096'}
                stroke="#1a202c"
                strokeWidth={1}
              />
              <Text
                x={seat.x - 6}
                y={seat.y - 5}
                text={guest ? guest.guest_name.charAt(0).toUpperCase() : String(seat.number)}
                fill="white"
                fontSize={10}
                fontStyle="bold"
              />
            </Group>
          );
        })}
      </Group>
      
      {isSelected && (
        <Transformer
          ref={trRef}
          rotateEnabled={true}
          enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 30 || newBox.height < 30) {
              return oldBox;
            }
            return newBox;
          }}
        />
      )}
    </>
  );
};

// Main Editor Component
export default function SeatingChartEditor({ bookingId, token, isClientPortal = false, onClose }) {
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  
  const [objects, setObjects] = useState([]);
  const [guests, setGuests] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [canvasSettings, setCanvasSettings] = useState({
    width: 1200,
    height: 800,
    zoom: 1,
    grid_enabled: true,
    grid_size: 20
  });
  const [chartName, setChartName] = useState('Seating Chart');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showAddObject, setShowAddObject] = useState(false);
  const [showEditObject, setShowEditObject] = useState(false);
  const [showGuestManager, setShowGuestManager] = useState(false);
  const [showAssignGuest, setShowAssignGuest] = useState(false);
  const [editingObject, setEditingObject] = useState(null);
  const [newGuestName, setNewGuestName] = useState('');
  const [selectedSeat, setSelectedSeat] = useState(null);
  
  // Stage dimensions
  const [stageSize, setStageSize] = useState({ width: 1200, height: 800 });
  
  // Load chart data
  useEffect(() => {
    if (bookingId || token) {
      loadChart();
    }
  }, [bookingId, token]);
  
  // Update stage size on container resize
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setStageSize({
          width: rect.width,
          height: Math.max(600, rect.height - 60)
        });
      }
    };
    
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  
  const loadChart = async () => {
    if (!bookingId && !token) {
      setLoading(false);
      return;
    }
    
    try {
      const url = isClientPortal 
        ? `${API}/seating/client-portal/${token}`
        : `${API}/seating/booking/${bookingId}`;
      
      const headers = !isClientPortal ? {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      } : {};
      
      const response = await axios.get(url, { headers });
      const data = response.data;
      
      setObjects(data.objects || []);
      setGuests(data.guests || []);
      setChartName(data.name || 'Seating Chart');
      if (data.canvas_settings) {
        setCanvasSettings(data.canvas_settings);
      }
    } catch (error) {
      console.error('Failed to load seating chart:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const saveChart = async () => {
    setSaving(true);
    try {
      const url = isClientPortal 
        ? `${API}/seating/client-portal/${token}`
        : `${API}/seating/booking/${bookingId}`;
      
      const headers = !isClientPortal ? {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      } : {};
      
      await axios.post(url, {
        name: chartName,
        objects,
        guests,
        canvas_settings: canvasSettings
      }, { headers });
      
      toast.success('Seating chart saved!');
    } catch (error) {
      toast.error('Failed to save seating chart');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };
  
  const addObject = (type) => {
    const config = OBJECT_TYPES[type];
    const newObj = {
      id: `obj_${Date.now()}`,
      type,
      x: stageSize.width / 2 / canvasSettings.zoom,
      y: stageSize.height / 2 / canvasSettings.zoom,
      width: config.defaultWidth,
      height: config.defaultHeight,
      rotation: 0,
      seats: config.defaultSeats,
      label: '',
      guests: []
    };
    setObjects([...objects, newObj]);
    setSelectedId(newObj.id);
    setShowAddObject(false);
  };
  
  const updateObject = (updatedObj) => {
    setObjects(objects.map(obj => 
      obj.id === updatedObj.id ? updatedObj : obj
    ));
  };
  
  const deleteObject = (id) => {
    setObjects(objects.filter(obj => obj.id !== id));
    setSelectedId(null);
    setShowEditObject(false);
  };
  
  const duplicateObject = (obj) => {
    const newObj = {
      ...obj,
      id: `obj_${Date.now()}`,
      x: obj.x + 50,
      y: obj.y + 50,
      label: obj.label ? `${obj.label} (Copy)` : ''
    };
    setObjects([...objects, newObj]);
    setSelectedId(newObj.id);
  };
  
  const handleObjectDblClick = (obj) => {
    setEditingObject(obj);
    setShowEditObject(true);
  };
  
  const addGuest = () => {
    if (!newGuestName.trim()) return;
    const newGuest = {
      id: `guest_${Date.now()}`,
      name: newGuestName.trim(),
      table_id: null,
      seat_number: null
    };
    setGuests([...guests, newGuest]);
    setNewGuestName('');
  };
  
  const removeGuest = (guestId) => {
    // Remove guest from any assigned seat
    const updatedObjects = objects.map(obj => ({
      ...obj,
      guests: (obj.guests || []).filter(g => g.guest_id !== guestId)
    }));
    setObjects(updatedObjects);
    setGuests(guests.filter(g => g.id !== guestId));
  };
  
  const assignGuestToSeat = (guestId, objectId, seatNumber) => {
    // Remove guest from previous seat if any
    const updatedObjects = objects.map(obj => ({
      ...obj,
      guests: (obj.guests || []).filter(g => g.guest_id !== guestId)
    }));
    
    // Assign to new seat
    const guest = guests.find(g => g.id === guestId);
    if (guest) {
      const finalObjects = updatedObjects.map(obj => {
        if (obj.id === objectId) {
          return {
            ...obj,
            guests: [...(obj.guests || []).filter(g => g.seat_number !== seatNumber), {
              guest_id: guestId,
              guest_name: guest.name,
              seat_number: seatNumber
            }]
          };
        }
        return obj;
      });
      setObjects(finalObjects);
      
      // Update guest record
      setGuests(guests.map(g => 
        g.id === guestId ? { ...g, table_id: objectId, seat_number: seatNumber } : g
      ));
    }
    setShowAssignGuest(false);
  };
  
  const unassignGuest = (guestId) => {
    const updatedObjects = objects.map(obj => ({
      ...obj,
      guests: (obj.guests || []).filter(g => g.guest_id !== guestId)
    }));
    setObjects(updatedObjects);
    setGuests(guests.map(g => 
      g.id === guestId ? { ...g, table_id: null, seat_number: null } : g
    ));
  };
  
  const handleZoom = (direction) => {
    const newZoom = direction === 'in' 
      ? Math.min(canvasSettings.zoom + 0.1, 2)
      : Math.max(canvasSettings.zoom - 0.1, 0.3);
    setCanvasSettings({ ...canvasSettings, zoom: newZoom });
  };
  
  const handlePrint = () => {
    const stage = stageRef.current;
    if (stage) {
      const dataUrl = stage.toDataURL({ pixelRatio: 2 });
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>${chartName} - Seating Chart</title>
            <style>
              body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
              img { max-width: 100%; height: auto; }
              @media print { body { margin: 0; } img { max-width: 100%; } }
            </style>
          </head>
          <body>
            <img src="${dataUrl}" />
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.onload = () => printWindow.print();
    }
  };
  
  const exportImage = () => {
    const stage = stageRef.current;
    if (stage) {
      const dataUrl = stage.toDataURL({ pixelRatio: 2 });
      const link = document.createElement('a');
      link.download = `${chartName.replace(/\s+/g, '_')}_seating_chart.png`;
      link.href = dataUrl;
      link.click();
    }
  };
  
  // Print guest list by table
  const printGuestList = () => {
    // Get tables with guests (only objects that have seats)
    const tablesWithGuests = objects
      .filter(obj => OBJECT_TYPES[obj.type]?.hasSeats && obj.seats > 0)
      .sort((a, b) => {
        // Sort by label or type name
        const labelA = a.label || OBJECT_TYPES[a.type]?.name || '';
        const labelB = b.label || OBJECT_TYPES[b.type]?.name || '';
        return labelA.localeCompare(labelB);
      });
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>${chartName} - Guest List</title>
          <style>
            * { box-sizing: border-box; }
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              max-width: 800px;
              margin: 0 auto;
              padding: 40px 20px;
              color: #1a1a1a;
            }
            .header {
              text-align: center;
              margin-bottom: 40px;
              padding-bottom: 20px;
              border-bottom: 2px solid #333;
            }
            .header h1 {
              margin: 0 0 8px 0;
              font-size: 28px;
              font-weight: 600;
            }
            .header .subtitle {
              color: #666;
              font-size: 14px;
            }
            .stats {
              display: flex;
              justify-content: center;
              gap: 30px;
              margin-top: 15px;
              font-size: 13px;
              color: #555;
            }
            .table-section {
              margin-bottom: 25px;
              break-inside: avoid;
            }
            .table-header {
              background: #f5f5f5;
              padding: 12px 16px;
              border-radius: 8px 8px 0 0;
              border: 1px solid #ddd;
              border-bottom: none;
            }
            .table-name {
              font-size: 16px;
              font-weight: 600;
              margin: 0;
            }
            .table-info {
              font-size: 12px;
              color: #666;
              margin-top: 4px;
            }
            .guest-list {
              border: 1px solid #ddd;
              border-radius: 0 0 8px 8px;
            }
            .guest-row {
              display: flex;
              justify-content: space-between;
              padding: 10px 16px;
              border-bottom: 1px solid #eee;
            }
            .guest-row:last-child {
              border-bottom: none;
            }
            .guest-name {
              font-size: 14px;
            }
            .seat-number {
              color: #666;
              font-size: 13px;
            }
            .empty-seats {
              padding: 12px 16px;
              color: #999;
              font-style: italic;
              font-size: 13px;
            }
            .unassigned-section {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 2px solid #ddd;
            }
            .unassigned-header {
              font-size: 18px;
              font-weight: 600;
              margin-bottom: 15px;
              color: #666;
            }
            .unassigned-list {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 8px;
            }
            .unassigned-guest {
              padding: 8px 12px;
              background: #fff3cd;
              border-radius: 4px;
              font-size: 13px;
            }
            @media print {
              body { padding: 20px; }
              .table-section { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${chartName}</h1>
            <div class="subtitle">Seating Arrangement</div>
            <div class="stats">
              <span><strong>${tablesWithGuests.length}</strong> Tables</span>
              <span><strong>${assignedGuests.length}</strong> Guests Seated</span>
              <span><strong>${unassignedGuests.length}</strong> Unassigned</span>
            </div>
          </div>
          
          ${tablesWithGuests.map(table => {
            const tableGuests = (table.guests || []).sort((a, b) => a.seat_number - b.seat_number);
            const tableName = table.label || OBJECT_TYPES[table.type]?.name || 'Table';
            return `
              <div class="table-section">
                <div class="table-header">
                  <h3 class="table-name">${tableName}</h3>
                  <div class="table-info">${tableGuests.length} of ${table.seats} seats filled</div>
                </div>
                <div class="guest-list">
                  ${tableGuests.length > 0 
                    ? tableGuests.map(guest => `
                        <div class="guest-row">
                          <span class="guest-name">${guest.guest_name}</span>
                          <span class="seat-number">Seat ${guest.seat_number}</span>
                        </div>
                      `).join('')
                    : '<div class="empty-seats">No guests assigned</div>'
                  }
                </div>
              </div>
            `;
          }).join('')}
          
          ${unassignedGuests.length > 0 ? `
            <div class="unassigned-section">
              <div class="unassigned-header">Unassigned Guests (${unassignedGuests.length})</div>
              <div class="unassigned-list">
                ${unassignedGuests.map(g => `
                  <div class="unassigned-guest">${g.name}</div>
                `).join('')}
              </div>
            </div>
          ` : ''}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };
  
  // Grid pattern
  const gridLines = [];
  if (canvasSettings.grid_enabled) {
    const gridSize = canvasSettings.grid_size;
    const width = canvasSettings.width;
    const height = canvasSettings.height;
    
    for (let i = 0; i <= width / gridSize; i++) {
      gridLines.push(
        <Line
          key={`v_${i}`}
          points={[i * gridSize, 0, i * gridSize, height]}
          stroke="#2d3748"
          strokeWidth={0.5}
        />
      );
    }
    for (let j = 0; j <= height / gridSize; j++) {
      gridLines.push(
        <Line
          key={`h_${j}`}
          points={[0, j * gridSize, width, j * gridSize]}
          stroke="#2d3748"
          strokeWidth={0.5}
        />
      );
    }
  }
  
  const selectedObject = objects.find(obj => obj.id === selectedId);
  const unassignedGuests = guests.filter(g => !g.table_id);
  const assignedGuests = guests.filter(g => g.table_id);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col h-full bg-gray-900 rounded-xl overflow-hidden" data-testid="seating-chart-editor">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-3 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <Input
            value={chartName}
            onChange={(e) => setChartName(e.target.value)}
            className="w-48 bg-gray-700 border-gray-600 text-white"
            placeholder="Chart Name"
          />
          
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowAddObject(true)}
            className="border-cyan-500 text-cyan-400 hover:bg-cyan-500/20"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Object
          </Button>
          
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowGuestManager(true)}
            className="border-purple-500 text-purple-400 hover:bg-purple-500/20"
          >
            <Users className="w-4 h-4 mr-1" />
            Guests ({guests.length})
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <Button size="icon" variant="ghost" onClick={() => handleZoom('out')} className="text-gray-400 hover:text-white">
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-sm text-gray-400 w-12 text-center">{Math.round(canvasSettings.zoom * 100)}%</span>
          <Button size="icon" variant="ghost" onClick={() => handleZoom('in')} className="text-gray-400 hover:text-white">
            <ZoomIn className="w-4 h-4" />
          </Button>
          
          <div className="w-px h-6 bg-gray-600 mx-2" />
          
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setCanvasSettings({ ...canvasSettings, grid_enabled: !canvasSettings.grid_enabled })}
            className={canvasSettings.grid_enabled ? 'text-cyan-400' : 'text-gray-400'}
          >
            <Grid3X3 className="w-4 h-4" />
          </Button>
          
          <Button size="icon" variant="ghost" onClick={handlePrint} className="text-gray-400 hover:text-white" title="Print Floor Plan">
            <Printer className="w-4 h-4" />
          </Button>
          
          <Button size="icon" variant="ghost" onClick={printGuestList} className="text-gray-400 hover:text-white" title="Print Guest List">
            <List className="w-4 h-4" />
          </Button>
          
          <Button size="icon" variant="ghost" onClick={exportImage} className="text-gray-400 hover:text-white" title="Download as Image">
            <Download className="w-4 h-4" />
          </Button>
          
          <div className="w-px h-6 bg-gray-600 mx-2" />
          
          <Button
            size="sm"
            onClick={saveChart}
            disabled={saving}
            className="bg-cyan-500 hover:bg-cyan-600 text-black"
          >
            <Save className="w-4 h-4 mr-1" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
          
          {onClose && (
            <Button size="icon" variant="ghost" onClick={onClose} className="text-gray-400 hover:text-white">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
      
      {/* Canvas Area */}
      <div ref={containerRef} className="flex-1 overflow-auto bg-gray-950">
        <Stage
          ref={stageRef}
          width={stageSize.width}
          height={stageSize.height}
          scaleX={canvasSettings.zoom}
          scaleY={canvasSettings.zoom}
          onClick={(e) => {
            if (e.target === e.target.getStage()) {
              setSelectedId(null);
            }
          }}
        >
          <Layer>
            {/* Background */}
            <Rect
              x={0}
              y={0}
              width={canvasSettings.width}
              height={canvasSettings.height}
              fill="#1a202c"
            />
            
            {/* Grid */}
            {gridLines}
            
            {/* Objects */}
            {objects.map(obj => (
              <TableShape
                key={obj.id}
                obj={obj}
                isSelected={selectedId === obj.id}
                onSelect={() => setSelectedId(obj.id)}
                onChange={updateObject}
                onDblClick={() => handleObjectDblClick(obj)}
              />
            ))}
          </Layer>
        </Stage>
      </div>
      
      {/* Selected Object Info Bar */}
      {selectedObject && (
        <div className="flex items-center justify-between p-3 bg-gray-800 border-t border-gray-700">
          <div className="flex items-center gap-4">
            <span className="text-white font-medium">
              {OBJECT_TYPES[selectedObject.type]?.name}: {selectedObject.label || 'Unnamed'}
            </span>
            {OBJECT_TYPES[selectedObject.type]?.hasSeats && (
              <span className="text-gray-400">
                Seats: {selectedObject.guests?.length || 0}/{selectedObject.seats}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleObjectDblClick(selectedObject)}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <Edit2 className="w-4 h-4 mr-1" />
              Edit
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => duplicateObject(selectedObject)}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Duplicate
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => deleteObject(selectedObject.id)}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
          </div>
        </div>
      )}
      
      {/* Add Object Dialog */}
      <Dialog open={showAddObject} onOpenChange={setShowAddObject}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Object to Chart</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3 py-4">
            {Object.entries(OBJECT_TYPES).map(([type, config]) => {
              const Icon = config.icon;
              return (
                <button
                  key={type}
                  onClick={() => addObject(type)}
                  className="flex flex-col items-center p-4 rounded-lg border border-gray-600 hover:border-cyan-500 hover:bg-gray-700 transition-colors"
                  style={{ borderLeftColor: config.color, borderLeftWidth: 4 }}
                >
                  <Icon className="w-8 h-8 mb-2" style={{ color: config.color }} />
                  <span className="text-sm">{config.name}</span>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Edit Object Dialog */}
      <Dialog open={showEditObject} onOpenChange={setShowEditObject}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Edit {editingObject && OBJECT_TYPES[editingObject.type]?.name}</DialogTitle>
          </DialogHeader>
          {editingObject && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-gray-300">Label</Label>
                <Input
                  value={editingObject.label}
                  onChange={(e) => setEditingObject({ ...editingObject, label: e.target.value })}
                  placeholder={OBJECT_TYPES[editingObject.type]?.name}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              
              {OBJECT_TYPES[editingObject.type]?.hasSeats && (
                <div>
                  <Label className="text-gray-300">Number of Seats</Label>
                  <Input
                    type="number"
                    value={editingObject.seats}
                    onChange={(e) => setEditingObject({ ...editingObject, seats: parseInt(e.target.value) || 0 })}
                    min={0}
                    max={20}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Width</Label>
                  <Input
                    type="number"
                    value={Math.round(editingObject.width)}
                    onChange={(e) => setEditingObject({ ...editingObject, width: parseInt(e.target.value) || 50 })}
                    min={30}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Height</Label>
                  <Input
                    type="number"
                    value={Math.round(editingObject.height)}
                    onChange={(e) => setEditingObject({ ...editingObject, height: parseInt(e.target.value) || 50 })}
                    min={30}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-gray-300">Rotation (degrees)</Label>
                <Input
                  type="number"
                  value={Math.round(editingObject.rotation || 0)}
                  onChange={(e) => setEditingObject({ ...editingObject, rotation: parseInt(e.target.value) || 0 })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              
              {/* Seat assignments */}
              {OBJECT_TYPES[editingObject.type]?.hasSeats && editingObject.seats > 0 && (
                <div>
                  <Label className="text-gray-300 mb-2 block">Seat Assignments</Label>
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {[...Array(editingObject.seats)].map((_, idx) => {
                      const seatNum = idx + 1;
                      const assigned = editingObject.guests?.find(g => g.seat_number === seatNum);
                      return (
                        <div key={seatNum} className="flex items-center justify-between p-2 bg-gray-700 rounded">
                          <span className="text-sm">Seat {seatNum}</span>
                          {assigned ? (
                            <div className="flex items-center gap-1">
                              <span className="text-sm text-green-400">{assigned.guest_name}</span>
                              <button
                                onClick={() => {
                                  const newGuests = editingObject.guests.filter(g => g.seat_number !== seatNum);
                                  setEditingObject({ ...editingObject, guests: newGuests });
                                }}
                                className="text-red-400 hover:text-red-300"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setSelectedSeat({ objectId: editingObject.id, seatNumber: seatNum });
                                setShowAssignGuest(true);
                              }}
                              className="text-xs px-2 py-1 bg-gray-600 rounded hover:bg-gray-500"
                            >
                              Assign
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditObject(false)} className="border-gray-600">
              Cancel
            </Button>
            <Button
              onClick={() => {
                updateObject(editingObject);
                setShowEditObject(false);
              }}
              className="bg-cyan-500 hover:bg-cyan-600 text-black"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Guest Manager Dialog */}
      <Dialog open={showGuestManager} onOpenChange={setShowGuestManager}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Manage Guests</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Add new guest */}
            <div className="flex gap-2">
              <Input
                value={newGuestName}
                onChange={(e) => setNewGuestName(e.target.value)}
                placeholder="Enter guest name"
                className="bg-gray-700 border-gray-600 text-white flex-1"
                onKeyDown={(e) => e.key === 'Enter' && addGuest()}
              />
              <Button onClick={addGuest} className="bg-cyan-500 hover:bg-cyan-600 text-black">
                <UserPlus className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Guest list */}
            <div className="space-y-2 max-h-80 overflow-y-auto">
              <div className="text-sm text-gray-400 font-medium">
                Unassigned ({unassignedGuests.length})
              </div>
              {unassignedGuests.map(guest => (
                <div key={guest.id} className="flex items-center justify-between p-2 bg-gray-700 rounded">
                  <span>{guest.name}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeGuest(guest.id)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              
              {assignedGuests.length > 0 && (
                <>
                  <div className="text-sm text-gray-400 font-medium mt-4">
                    Assigned ({assignedGuests.length})
                  </div>
                  {assignedGuests.map(guest => {
                    const table = objects.find(o => o.id === guest.table_id);
                    return (
                      <div key={guest.id} className="flex items-center justify-between p-2 bg-green-900/30 rounded">
                        <div>
                          <span>{guest.name}</span>
                          <span className="text-xs text-gray-400 ml-2">
                            → {table?.label || OBJECT_TYPES[table?.type]?.name}, Seat {guest.seat_number}
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => unassignGuest(guest.id)}
                          className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20"
                        >
                          Unassign
                        </Button>
                      </div>
                    );
                  })}
                </>
              )}
              
              {guests.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  No guests added yet. Add guests above to assign them to seats.
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Assign Guest to Seat Dialog */}
      <Dialog open={showAssignGuest} onOpenChange={setShowAssignGuest}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Assign Guest to Seat {selectedSeat?.seatNumber}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-4 max-h-60 overflow-y-auto">
            {unassignedGuests.length === 0 ? (
              <div className="text-center text-gray-500 py-4">
                No unassigned guests. Add guests in the Guest Manager first.
              </div>
            ) : (
              unassignedGuests.map(guest => (
                <button
                  key={guest.id}
                  onClick={() => {
                    if (selectedSeat) {
                      // Update the editing object directly
                      const newGuests = [...(editingObject?.guests || []), {
                        guest_id: guest.id,
                        guest_name: guest.name,
                        seat_number: selectedSeat.seatNumber
                      }];
                      setEditingObject({ ...editingObject, guests: newGuests });
                      
                      // Update the main objects array
                      setObjects(objects.map(obj => 
                        obj.id === selectedSeat.objectId 
                          ? { ...obj, guests: newGuests }
                          : obj
                      ));
                      
                      // Update guest record
                      setGuests(guests.map(g => 
                        g.id === guest.id 
                          ? { ...g, table_id: selectedSeat.objectId, seat_number: selectedSeat.seatNumber }
                          : g
                      ));
                    }
                    setShowAssignGuest(false);
                  }}
                  className="w-full p-3 text-left bg-gray-700 rounded hover:bg-gray-600 transition-colors"
                >
                  {guest.name}
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
